/**
 * Sidebar — Navigation hierarchique basee sur l'arborescence Node
 *
 * Utilise /api/nodes/tree pour afficher l'arbre complet.
 * Le champ `groupe` (ex: "Transits de Pluton > Transits de Pluton en Maisons")
 * est utilise pour reconstruire une hierarchie visuelle a partir de donnees plates.
 */

import { useState, useEffect, useMemo } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { apiFetch } from '../../utils/api.js';
import { useSettings } from '../../contexts/ThemeContext.js';
import { useAuth } from '../../contexts/AuthContext.js';
import type { NodeTree } from '../../types/courses.js';
import { colors } from '../../styles/tokens.js';

// ─── Couleurs ────────────────────────────────────────────
const C = {
  dore: colors.accent,
  dorePale: colors.accentLight,
  doreLight: `${colors.accent}14`,
  fond: colors.bgPage,
  fondHover: '#f5f5f5',
  blanc: colors.bgContent,
  texte: colors.text,
  texteClair: colors.textMid,
  textePale: colors.textMuted,
  bordure: colors.border,
  bordureLeg: '#f0f0f0',
};

// ─── Helpers ─────────────────────────────────────────────

function containsActiveId(nodes: NodeTree[], activeId: number | null): boolean {
  if (!activeId) return false;
  for (const n of nodes) {
    if (n.id === activeId) return true;
    if (n.children?.length && containsActiveId(n.children, activeId)) return true;
  }
  return false;
}

function containsActiveNode(node: NodeTree, activeId: number | null): boolean {
  if (!activeId) return false;
  if (node.id === activeId) return true;
  return node.children?.some((c) => containsActiveNode(c, activeId)) ?? false;
}

// ─── Regroupement visuel par champ `groupe` ──────────────

interface VisualGroup {
  label: string;
  headerNode: NodeTree | null; // Le noeud dont le nom == label du groupe (cliquable)
  children: (NodeTree | VisualGroup)[];
}

function isVisualGroup(item: NodeTree | VisualGroup): item is VisualGroup {
  return 'label' in item && 'headerNode' in item;
}

/**
 * Reconstruit une hierarchie visuelle a partir d'une liste plate de noeuds
 * en utilisant le champ `groupe` (paths separes par ` > `).
 *
 * Exemple : groupe="Transits de Pluton > Transits de Pluton en Maisons"
 * → crée un groupe "Transits de Pluton" contenant un sous-groupe "Transits de Pluton en Maisons"
 */
function buildVisualGroups(nodes: NodeTree[]): (NodeTree | VisualGroup)[] {
  // Verifier si les noeuds ont un champ groupe
  const hasGroups = nodes.some((n) => n.groupe);
  if (!hasGroups) return nodes;

  // Creer une map de groupes imbriques
  const rootItems: (NodeTree | VisualGroup)[] = [];
  const groupMap = new Map<string, VisualGroup>();

  function getOrCreateGroup(path: string): VisualGroup {
    if (groupMap.has(path)) return groupMap.get(path)!;

    const parts = path.split(' > ');
    const label = parts[parts.length - 1]!;
    const group: VisualGroup = { label, headerNode: null, children: [] };
    groupMap.set(path, group);

    if (parts.length > 1) {
      const parentPath = parts.slice(0, -1).join(' > ');
      const parent = getOrCreateGroup(parentPath);
      // Ajouter seulement si pas déjà présent
      if (!parent.children.some((c) => isVisualGroup(c) && c.label === label)) {
        parent.children.push(group);
      }
    } else {
      // Groupe racine — ajouter a rootItems si pas deja present
      if (!rootItems.some((i) => isVisualGroup(i) && i.label === label)) {
        rootItems.push(group);
      }
    }

    return group;
  }

  // Iterer dans l'ordre pour preserver l'ordre d'insertion
  for (const node of nodes) {
    if (!node.groupe) {
      // Noeud sans groupe → va a la racine
      rootItems.push(node);
      continue;
    }

    const group = getOrCreateGroup(node.groupe);

    // Si le nom du noeud correspond au label du groupe, c'est le header
    if (node.nom === group.label) {
      group.headerNode = node;
    } else {
      group.children.push(node);
    }
  }

  // Nettoyer : si un groupe n'a qu'un headerNode et pas d'enfants, le remplacer par le noeud
  function simplify(items: (NodeTree | VisualGroup)[]): (NodeTree | VisualGroup)[] {
    return items.map((item) => {
      if (!isVisualGroup(item)) return item;
      item.children = simplify(item.children);
      if (item.children.length === 0 && item.headerNode) {
        return item.headerNode;
      }
      return item;
    });
  }

  return simplify(rootItems);
}

/** Compte toutes les fiches (feuilles reelles) dans un groupe visuel recursivement */
function countLeaves(items: (NodeTree | VisualGroup)[]): number {
  let count = 0;
  for (const item of items) {
    if (isVisualGroup(item)) {
      if (item.headerNode) count++; // le header est aussi une fiche
      count += countLeaves(item.children);
    } else {
      count++;
    }
  }
  return count;
}

function visualGroupContainsActive(items: (NodeTree | VisualGroup)[], activeId: number | null): boolean {
  if (!activeId) return false;
  for (const item of items) {
    if (isVisualGroup(item)) {
      if (item.headerNode?.id === activeId) return true;
      if (visualGroupContainsActive(item.children, activeId)) return true;
    } else {
      if (item.id === activeId) return true;
      if (item.children?.length && containsActiveId(item.children, activeId)) return true;
    }
  }
  return false;
}

// ─── Composants ──────────────────────────────────────────

/** Badge compteur */
function Badge({ n, active }: { n: number; active?: boolean }) {
  if (!n) return null;
  return (
    <span
      style={{
        background: active ? C.dore : '#f0f0f0',
        color: active ? '#fff' : C.textePale,
        fontSize: '0.7rem',
        fontWeight: 600,
        padding: '1px 7px',
        borderRadius: 10,
        flexShrink: 0,
        lineHeight: '16px',
        minWidth: 20,
        textAlign: 'center',
      }}
    >
      {n}
    </span>
  );
}

/** Petit chevron */
function SmallChevron({ open }: { open: boolean }) {
  return (
    <i
      className="bi bi-chevron-down"
      style={{
        fontSize: '0.5rem',
        color: '#ccc',
        transition: 'transform 0.2s',
        transform: open ? 'rotate(0deg)' : 'rotate(-90deg)',
        display: 'inline-block',
        flexShrink: 0,
      }}
    />
  );
}

/**
 * Style unifie par profondeur — meme apparence pour fiche seule ou groupe.
 * La seule difference : les groupes affichent un chevron + badge.
 */
function depthStyle(depth: number, active: boolean) {
  return {
    fontSize: '0.78rem',
    fontWeight: active ? 600 : 400,
    color: active ? C.dore : depth === 0 ? C.texteClair : C.textePale,
  };
}

/** Conteneur enfants avec trait vertical */
function ChildrenContainer({ children, depth }: { children: React.ReactNode; depth: number }) {
  return (
    <div
      style={{
        marginLeft: 12,
        paddingLeft: 12,
        borderLeft: `1px solid ${depth === 0 ? C.bordure : C.bordureLeg}`,
        paddingBottom: 2,
      }}
    >
      {children}
    </div>
  );
}

/** Ligne cliquable — utilisee par Leaf, VisualGroupItem et RealNodeBranch */
function ItemRow({
  label,
  nodeId,
  depth,
  active,
  expandable,
  open,
  badgeCount,
  onSelect,
  onToggle,
}: {
  label: string;
  nodeId: number | null;
  depth: number;
  active: boolean;
  expandable: boolean;
  open?: boolean;
  badgeCount?: number;
  onSelect: (id: number) => void;
  onToggle?: () => void;
}) {
  const style = depthStyle(depth, active);
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        padding: '5px 12px',
        cursor: 'pointer',
        background: active ? C.doreLight : 'transparent',
        transition: 'background 0.15s',
      }}
      onClick={() => {
        if (expandable && onToggle) onToggle();
        else if (nodeId) onSelect(nodeId);
      }}
      onMouseEnter={(e) => {
        if (!active) e.currentTarget.style.background = C.fondHover;
      }}
      onMouseLeave={(e) => {
        if (!active) e.currentTarget.style.background = active ? C.doreLight : 'transparent';
      }}
    >
      {nodeId ? (
        <button
          type="button"
          onClick={(e) => {
            if (expandable) e.stopPropagation();
            onSelect(nodeId);
          }}
          style={{
            flex: 1,
            border: 'none',
            background: 'none',
            padding: 0,
            cursor: 'pointer',
            textAlign: 'left',
            fontFamily: 'inherit',
            transition: 'color 0.15s',
            ...style,
          }}
          onMouseEnter={(e) => { e.currentTarget.style.color = C.dore; }}
          onMouseLeave={(e) => { if (!active) e.currentTarget.style.color = style.color; }}
        >
          {label}
        </button>
      ) : (
        <span style={{ flex: 1, ...style }}>{label}</span>
      )}
      {expandable && badgeCount != null && badgeCount > 0 && (
        <Badge n={badgeCount} active={open} />
      )}
      {expandable && <SmallChevron open={!!open} />}
    </div>
  );
}

/** Feuille simple (dernier niveau) */
function Leaf({
  node,
  depth,
  active,
  onSelect,
}: {
  node: NodeTree;
  depth: number;
  active: boolean;
  onSelect: (id: number) => void;
}) {
  return (
    <ItemRow
      label={node.nom}
      nodeId={node.id}
      depth={depth}
      active={active}
      expandable={false}
      onSelect={onSelect}
    />
  );
}

/**
 * Rendu recursif d'une liste d'items visuels (groupes + feuilles).
 * Comportement accordeon : un seul item ouvert a la fois par niveau.
 */
function VisualItemList({
  items,
  depth,
  activeId,
  onSelect,
}: {
  items: (NodeTree | VisualGroup)[];
  depth: number;
  activeId: number | null;
  onSelect: (id: number) => void;
}) {
  // Trouver l'index de l'item qui contient le noeud actif (pour l'ouvrir automatiquement)
  const activeIndex = useMemo(() => {
    if (!activeId) return null;
    for (let i = 0; i < items.length; i++) {
      const item = items[i]!;
      if (isVisualGroup(item)) {
        if (item.headerNode?.id === activeId) return i;
        if (visualGroupContainsActive(item.children, activeId)) return i;
      } else {
        const node = item as NodeTree;
        if (containsActiveNode(node, activeId)) return i;
      }
    }
    return null;
  }, [items, activeId]);

  const [openIndex, setOpenIndex] = useState<number | null>(activeIndex);

  useEffect(() => {
    if (activeIndex !== null) setOpenIndex(activeIndex);
  }, [activeIndex]);

  function handleToggle(i: number) {
    setOpenIndex((prev) => (prev === i ? null : i));
  }

  return (
    <>
      {items.map((child, i) => {
        if (isVisualGroup(child)) {
          const headerActive = child.headerNode?.id === activeId;
          const leafCount = countLeaves(child.children);
          const isOpen = openIndex === i;
          return (
            <div key={`vg-${child.label}-${i}`}>
              <ItemRow
                label={child.label}
                nodeId={child.headerNode?.id ?? null}
                depth={depth}
                active={headerActive}
                expandable={true}
                open={isOpen}
                badgeCount={leafCount}
                onSelect={onSelect}
                onToggle={() => handleToggle(i)}
              />
              {isOpen && (
                <ChildrenContainer depth={depth}>
                  <VisualItemList
                    items={child.children}
                    depth={depth + 1}
                    activeId={activeId}
                    onSelect={onSelect}
                  />
                </ChildrenContainer>
              )}
            </div>
          );
        }
        const childNode = child as NodeTree;
        const hasChildren = childNode.children && childNode.children.length > 0;
        if (hasChildren) {
          const selfActive = childNode.id === activeId;
          const isOpen = openIndex === i;
          const visualChildren = buildVisualGroups(childNode.children || []);
          return (
            <div key={childNode.id}>
              <ItemRow
                label={childNode.nom}
                nodeId={childNode.id}
                depth={depth}
                active={selfActive}
                expandable={true}
                open={isOpen}
                badgeCount={childNode.children?.length ?? 0}
                onSelect={onSelect}
                onToggle={() => handleToggle(i)}
              />
              {isOpen && (
                <ChildrenContainer depth={depth}>
                  <VisualItemList
                    items={visualChildren}
                    depth={depth + 1}
                    activeId={activeId}
                    onSelect={onSelect}
                  />
                </ChildrenContainer>
              )}
            </div>
          );
        }
        return (
          <Leaf
            key={childNode.id}
            node={childNode}
            depth={depth}
            active={childNode.id === activeId}
            onSelect={onSelect}
          />
        );
      })}
    </>
  );
}

/** Section racine — UPPERCASE + icone + chevron */
function RootSection({
  node,
  activeId,
  open,
  onToggle,
  onSelect,
}: {
  node: NodeTree;
  activeId: number | null;
  open: boolean;
  onToggle: () => void;
  onSelect: (nodeId: number) => void;
}) {
  const hasChildren = node.children && node.children.length > 0;

  // Si un seul enfant et meme nom que le parent, masquer le niveau intermediaire
  const singleChild = node.children?.length === 1 ? node.children[0] : null;
  const skipChild =
    singleChild != null &&
    singleChild.nom.toLowerCase().trim() === node.nom.toLowerCase().trim();

  const effectiveChildren = skipChild && singleChild
    ? (singleChild.children || [])
    : (node.children || []);

  // Regrouper visuellement les enfants
  const visualChildren = useMemo(
    () => buildVisualGroups(effectiveChildren),
    [effectiveChildren],
  );

  return (
    <div style={{ borderBottom: `1px solid ${C.bordure}` }}>
      {/* Header UPPERCASE */}
      <div
        onClick={onToggle}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => e.key === 'Enter' && onToggle()}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.6rem',
          padding: '0.85rem 1.25rem',
          cursor: 'pointer',
          fontWeight: 600,
          fontSize: '0.82rem',
          textTransform: 'uppercase',
          letterSpacing: '0.06em',
          color: C.textePale,
          background: C.fond,
          transition: 'all 0.2s ease',
          userSelect: 'none',
        }}
        onMouseEnter={(e) => { e.currentTarget.style.background = '#f0f0f0'; }}
        onMouseLeave={(e) => { e.currentTarget.style.background = C.fond; }}
      >
        {node.icone && (
          <i className={`bi ${node.icone}`} style={{ fontSize: '0.82rem' }} />
        )}
        <span style={{ flex: 1 }}>{node.nom}</span>
        {hasChildren && <SmallChevron open={open} />}
      </div>

      {/* Contenu */}
      {open && visualChildren.length > 0 && (
        <div style={{ paddingLeft: 12, paddingBottom: '0.35rem' }}>
          <VisualItemList
            items={visualChildren}
            depth={0}
            activeId={activeId}
            onSelect={onSelect}
          />
        </div>
      )}
    </div>
  );
}

// ─── Sidebar principale ──────────────────────────────────

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

export function Sidebar({ isOpen, onClose }: SidebarProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const settings = useSettings();
  const { user } = useAuth();

  const [tree, setTree] = useState<NodeTree[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [openRootIndex, setOpenRootIndex] = useState<number | null>(null);

  const activeId = useMemo(() => {
    const match = location.pathname.match(/\/cours\/(\d+)/);
    if (match) return parseInt(match[1]!, 10);
    return null;
  }, [location.pathname]);

  // Ouvrir automatiquement la section qui contient le noeud actif
  useEffect(() => {
    if (activeId && tree.length > 0) {
      const idx = tree.findIndex((root) => containsActiveNode(root, activeId));
      if (idx >= 0) setOpenRootIndex(idx);
    }
  }, [activeId, tree]);

  useEffect(() => {
    apiFetch<NodeTree[]>('/nodes/tree')
      .then((res) => {
        if (res.success && res.data) {
          setTree(res.data);
        }
      })
      .finally(() => setIsLoading(false));
  }, []);

  function handleSelectNode(nodeId: number) {
    navigate(`/cours/${nodeId}`);
    onClose();
  }

  function isFooterActive(href: string) {
    return location.pathname === href || location.pathname.startsWith(href + '/');
  }

  const footerLinks = [
    ...(user?.role === 'ADMIN'
      ? [
          { id: 'nouvelle-fiche', label: 'Nouvelle fiche', href: '/cours/nouveau', icon: 'bi-plus-circle' },
          { id: 'gestion-cours', label: 'Gerer les cours', href: '/cours/gestion', icon: 'bi-list-check' },
        ]
      : []),
    { id: 'clients', label: 'Clients', href: '/clients', icon: 'bi-people' },
    { id: 'outils', label: 'Outils', href: '/tools', icon: 'bi-wrench' },
    { id: 'generateur', label: 'Generateur', href: '/generator', icon: 'bi-file-earmark-zip' },
    ...(user?.role === 'ADMIN'
      ? [
          { id: 'config', label: 'Configuration', href: '/settings', icon: 'bi-gear' },
          { id: 'calculs', label: 'Calculs', href: '/admin/calculs', icon: 'bi-sliders' },
        ]
      : []),
  ];

  return (
    <>
      {/* Overlay mobile */}
      {isOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 md:hidden"
          onClick={onClose}
        />
      )}

      <aside
        className={`
          fixed top-0 left-0 z-50 h-full flex flex-col
          md:translate-x-0 md:static md:z-auto
          transition-transform duration-300
          ${isOpen ? 'translate-x-0' : '-translate-x-full'}
        `}
        style={{
          width: 260,
          minWidth: 260,
          background: C.blanc,
          borderRight: `1px solid ${C.bordure}`,
          fontFamily: "'Inter', -apple-system, sans-serif",
        }}
      >
        {/* Logo */}
        <div
          style={{
            padding: '14px 20px',
            borderBottom: `1px solid ${C.bordure}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Link
            to="/dashboard"
            onClick={onClose}
            style={{ display: 'flex', alignItems: 'center', gap: 10, textDecoration: 'none' }}
          >
            {settings.logoUrl ? (
              <img
                src={settings.logoUrl}
                alt={settings.siteName}
                style={{ height: 36, width: 36, objectFit: 'contain' }}
              />
            ) : (
              <div
                style={{
                  width: 36,
                  height: 36,
                  background: C.dore,
                  borderRadius: 8,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '1.2rem',
                  color: '#fff',
                }}
              >
                ✧
              </div>
            )}
            <div>
              <div style={{ fontSize: 14, fontWeight: 700, color: C.texte }}>
                {settings.siteName}
              </div>
              <div style={{ fontSize: 10, color: C.texteClair, letterSpacing: '0.04em' }}>
                {settings.siteTagline}
              </div>
            </div>
          </Link>
          <button
            className="md:hidden p-1 rounded"
            onClick={onClose}
            aria-label="Fermer le menu"
            style={{ background: 'none', border: 'none', cursor: 'pointer' }}
          >
            <i className="bi bi-x-lg" style={{ fontSize: 16, color: C.textePale }} />
          </button>
        </div>

        {/* Lien Dashboard */}
        <div style={{ padding: '8px 10px', borderBottom: `1px solid ${C.bordure}` }}>
          <Link
            to="/dashboard"
            onClick={onClose}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              padding: '6px 10px',
              borderRadius: 6,
              textDecoration: 'none',
              background: location.pathname === '/dashboard' ? C.fondHover : 'transparent',
              color: location.pathname === '/dashboard' ? C.texte : C.texteClair,
              fontSize: '0.78rem',
              fontWeight: location.pathname === '/dashboard' ? 500 : 400,
            }}
          >
            <i className="bi bi-house-door" style={{ fontSize: '0.82rem' }} />
            Tableau de bord
          </Link>
        </div>

        {/* Navigation cours (scrollable) */}
        <div style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden' }}>
          {isLoading ? (
            <div style={{ padding: '16px 20px', fontSize: '0.78rem', color: C.textePale }}>
              Chargement...
            </div>
          ) : (
            tree.map((rootNode, i) => (
              <RootSection
                key={rootNode.id}
                node={rootNode}
                activeId={activeId}
                open={openRootIndex === i}
                onToggle={() => setOpenRootIndex((prev) => (prev === i ? null : i))}
                onSelect={handleSelectNode}
              />
            ))
          )}
        </div>

        {/* Footer */}
        <div style={{ borderTop: `1px solid ${C.bordure}`, padding: '8px 10px' }}>
          {footerLinks.map((item) => (
            <Link
              key={item.id}
              to={item.href}
              onClick={onClose}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                padding: '6px 10px',
                borderRadius: 6,
                textDecoration: 'none',
                background: isFooterActive(item.href) ? C.fondHover : 'transparent',
                color: isFooterActive(item.href) ? C.dore : C.texteClair,
                fontSize: '0.78rem',
                fontWeight: isFooterActive(item.href) ? 500 : 400,
                transition: 'all 0.2s ease',
              }}
              onMouseEnter={(e) => {
                if (!isFooterActive(item.href))
                  e.currentTarget.style.background = C.fondHover;
              }}
              onMouseLeave={(e) => {
                if (!isFooterActive(item.href))
                  e.currentTarget.style.background = 'transparent';
              }}
            >
              <i className={`bi ${item.icon}`} style={{ fontSize: '0.82rem' }} />
              {item.label}
            </Link>
          ))}
        </div>
      </aside>
    </>
  );
}
